import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import has from 'lodash.has';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Col, Div, ExpanderColumn, Icon, Row, Tag, Text } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';
import { appActions, bcActions, segmentsListActions } from 'src/store/actions';
import { ApiWrapper, Button, DeprecatedTag, GridWrapper } from 'src/components';
import { Page } from 'src/containers';
import useSegments from 'src/hooks/swr/useSegments';
import usePlatformConfig from 'src/hooks/usePlatformConfig';
import { all, any, checkHiddenByRole, formatNumber, getSegmentTagStyles, checkSufficientRole } from 'src/functions';
import { fetchOnboardings, getFileUploadUrl, uploadFileLowSec, validateReplacedOutcomes } from 'src/store/api';

import {
  CreateActuarialModelDialog,
  CreateClassifierModelDialog,
  DeleteSegmentDialog,
  DeleteSegmentDownloadDialog,
  DownloadSegmentDialog,
  VisibilitySegmentDownloadDialog,
  ScoreSegmentDialog as PredictModelDialog,
} from 'src/components/dialogs';
import SegmentCard from './card';
import { DeleteComponent, DownloadComponent } from './components';
import { CreateModelFeatureToggle, PredictModelFeatureToggle } from './feature_toggles';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

function checkApiStatus(error, isLoading) {
  if (error) return CONSTANTS.API.FAILED;
  if (isLoading) return CONSTANTS.API.STARTED;
  return CONSTANTS.API.SUCCEEDED;
}

const editEntityName = (entity, name, editDataset, editSegment, editCluster) => {
  const id = get(entity, 'id');
  const filter = get(entity, 'filter');
  const type = get(entity, 'type');
  if (filter === 'POPULATION_SNAPSHOT') {
    editDataset(get(entity, 'dataset.id'), { name });
  } else if (type === 'clusters') {
    editCluster(get(entity, 'segmentId'), id, { name });
  } else {
    editSegment(id, { name });
  }
};

function editEntityTag(entity, tag, editSegment, editCluster) {
  const type = get(entity, 'type');
  const id = get(entity, 'id');
  if (type === 'clusters') {
    editCluster(get(entity, 'segmentId'), id, { tag });
  } else {
    editSegment(id, { tag });
  }
}

function Segments({
  history,
  app: {
    profile,
    profile: { role, siteRole, tenant, originalTenant },
  },
  segmentsList: {
    addModelTargetStatus,
    client,
    controlledGridState,
    createModelStatus,
    deleteDownloadStatus,
    deleteSegmentStatus,
    downloadUrl,
    editClusterStatus,
    editDatasetStatus,
    editSegmentStatus,
    fetchDownloadUrlStatus,
    gridState,
    maxGridRowSelection,
    selectedClusterIds,
    selectedSegmentIds,
    checkDeleteSegmentDependents,
  },
  addModelTarget,
  createModel,
  deleteCluster,
  toggleVisibilityDownload,
  deleteDownload,
  deleteSegment,
  checkDeleteSegment,
  deselectAllSegmentIds,
  deselectGridRow,
  editCluster,
  editDataset,
  editSegment,
  enqueueNotification,
  fetchClientByTenant,
  fetchDownloadUrl,
  reset,
  unset,
  selectGridRow,
  setGridState,
  setSegmentId,
}) {
  // Element visibility
  const [showCreateActuarialModal, setShowCreateActuarialModal] = useState(false);
  const [showCreateClassifierModal, setShowCreateClassifierModal] = useState(false);
  const [showDeleteEntityModal, setShowDeleteEntityModal] = useState(false);
  const [showDeleteDownloadModal, setShowDeleteDownloadModal] = useState(false);
  const [showVisibilityDownloadModal, setShowVisibilityDownloadModal] = useState(false);
  const [showEntityDownloadsModal, setShowEntityDownloadsModal] = useState(false);
  const [showPredictModal, setShowPredictModal] = useState(false);

  // Permissions
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSandboxViewer, setIsSandboxViewer] = useState(false);

  // Misc
  const [filterTag, setFilterTag] = useState('');
  const [view, setView] = useState('LIST_VIEW');
  const [downloadEntity, setDownloadEntity] = useState(null);
  const [downloadName, setDownloadName] = useState(null);
  const [downloadDeleteId, setDownloadDeleteId] = useState(null);
  const [downloadVisibilityId, setDownloadVisibilityId] = useState(null);
  const [deleteEntity, setDeleteEntity] = useState(null);

  // Data
  const getQuery = useCallback(() => {
    const selected = [];
    if ([CONSTANTS.SEGMENT.FILTERS.ALL, ''].includes(filterTag)) {
      selected.push({
        name: 'tag',
        op: 'in',
        val: CONSTANTS.SEGMENT.FILTER.ALL,
      });
    } else {
      selected.push({
        name: 'tag',
        op: 'in',
        val: CONSTANTS.SEGMENT.FILTER[filterTag],
      });
    }
    const query = {
      filters: [
        {
          name: 'is_hidden',
          op: 'eq',
          val: false,
        },
        {
          name: 'filter',
          op: 'not_in',
          val: [
            'CLUSTERING_V3/2',
            'CLUSTERING_V3/3',
            'CLUSTERING_V3/4',
            'CLUSTERING_V3/5',
            'CLUSTERING_V3/6',
            'CLUSTERING_V3/2/SELECTED',
            'CLUSTERING_V3/3/SELECTED',
            'CLUSTERING_V3/4/SELECTED',
            'CLUSTERING_V3/5/SELECTED',
            'CLUSTERING_V3/6/SELECTED',
          ],
        },
        ...selected,
      ],
      order_by: [
        {
          field: 'created_at',
          direction: 'desc',
        },
      ],
      fields:
        'items{id,filter,tag,name,users,matched,downloads,theme,statuses,created_at,createdAt,refreshId,refresh{id,name,platform},datasetId,dataset{id,name,payload{onb_validation_estimate}},clusters{id,filter,segmentId,name,tag,matched,downloads,users,theme,created_at}},*',
    };
    return query;
  }, [filterTag]);

  const {
    data: segments = [],
    error,
    isLoading,
    mutate: getData,
  } = useSegments({ query: getQuery(), config: { refreshInterval: 30000 } });

  const numberSort = useMemo(
    () => (rowA, rowB, columnId) => {
      const a = Number(rowA.values[columnId]);
      const b = Number(rowB.values[columnId]);
      return a > b ? 1 : -1;
    },
    []
  );

  const percentSort = useMemo(
    () => (rowA, rowB, columnId) => {
      const a = parseFloat(rowA.values[columnId] ?? '0');
      const b = parseFloat(rowB.values[columnId] ?? '0');
      return a > b ? 1 : -1;
    },
    []
  );

  const data = useMemo(() => {
    const completedSegments = segments.filter(({ statuses }) =>
      statuses.find(({ status }) => status === CONSTANTS.SEGMENT.STATUS.NOTIFICATION_SUCCESS)
    );
    const typedSegments = completedSegments.map((segment, parentIdx) => ({
      ...segment,
      type: 'segments',
      downloads: segment.downloads
        .map((dl) => ({ ...dl, entityId: segment.id, type: 'segments' }))
        .filter((dl) => !checkHiddenByRole(siteRole || role, dl.hiddenByRole)),
      subRows: get(segment, 'clusters', [])
        .filter((elem) => get(elem, 'filter').match(/ACTUARIAL_\w*|CLUSTERING_V\d+\/\d+\/SELECTED/))
        .map((elem) => ({
          ...elem,
          type: 'clusters',
          parentIdx,
          downloads: elem.downloads
            .map((dl) => ({ ...dl, type: 'clusters', entityId: elem.id }))
            .filter((dl) => !checkHiddenByRole(siteRole || role, dl.hiddenByRole)),
          refresh: segment.refresh,
        })),
    }));
    const list = typedSegments.map((segment, idx) => {
      const next = segment;
      if (next.tag === CONSTANTS.SEGMENT.TAG.ACTUARIAL) {
        segment.subRows.forEach((cluster) => {
          next.downloads = next.downloads.concat(cluster.downloads);
        });
      }
      return next;
    });
    return list;
  }, [segments, siteRole, role]);

  // State selectors
  const totalSelected = useMemo(
    () => selectedSegmentIds.length + selectedClusterIds.length,
    [selectedSegmentIds, selectedClusterIds]
  );

  const hasClusters = useMemo(() => {
    if (totalSelected === 1 && selectedSegmentIds.length === 1) {
      const segment = segments.find(({ id }) => id === selectedSegmentIds[0].segmentId);
      return segment && segment.clusters.length > 0;
    }
    return false;
  }, [totalSelected, selectedSegmentIds, segments]);

  const platformConfig = usePlatformConfig();

  const isDeprecatedPlatform = useMemo(() => {
    const usesActivePlatform = selectedSegmentIds.some(({ segmentId }) => {
      const { platform } = segments.find(({ id }) => id === segmentId)?.refresh ?? {};
      return platformConfig.canAction(platform);
    });
    return !usesActivePlatform;
  }, [selectedSegmentIds, segments, platformConfig]);

  const isUpgradedPlatform = useMemo(() => {
    const selectedSegments = [...selectedSegmentIds, ...selectedClusterIds].map(({ segmentId }) =>
      segments.find(({ id }) => id === segmentId)
    );
    return selectedSegments.some((segment) => {
      const { platform } = segment?.refresh ?? {};
      return platformConfig.canActionUpgradedPlatform(platform);
    });
  }, [platformConfig, segments, selectedSegmentIds, selectedClusterIds]);

  const isClassifierModelValid = useMemo(() => {
    const shouldCheckSegments = all([
      totalSelected > 1,
      totalSelected <= 3,
      selectedSegmentIds.length > 1,
      selectedSegmentIds.length <= 3,
    ]);
    if (!shouldCheckSegments) return false;
    let hitSeen = false;
    let missSeen = false;
    let validationSeen = false;
    let isValid = true;
    selectedSegmentIds.forEach(({ segmentId }) => {
      const { tag } = segments.find(({ id }) => id === segmentId);
      switch (tag) {
        case CONSTANTS.SEGMENT.TAG.HIT: {
          if (hitSeen) {
            isValid = false;
          }
          hitSeen = true;
          break;
        }
        case CONSTANTS.SEGMENT.TAG.MISS: {
          if (missSeen) {
            isValid = false;
          }
          missSeen = true;
          break;
        }
        case CONSTANTS.SEGMENT.TAG.VALIDATION: {
          if (validationSeen) {
            isValid = false;
          }
          validationSeen = true;
          break;
        }
        default: {
          isValid = false;
        }
      }
    });
    return all([hitSeen, missSeen, isValid]);
  }, [totalSelected, selectedSegmentIds, segments]);

  const isPredictModelValid = useMemo(() => {
    if (totalSelected === 1 && selectedSegmentIds.length === 1) {
      const [{ segmentId }] = selectedSegmentIds;
      const { tag } = segments.find(({ id }) => id === segmentId);
      if (CONSTANTS.SEGMENT.TAG.TARGET === tag) return true;
      if (
        CONSTANTS.SEGMENT.TAG.ACTUARIAL === tag &&
        checkSufficientRole(siteRole, CONSTANTS.ROLE.CLIENT_SERVICES, true)
      )
        return true;
    }
    return false;
  }, [totalSelected, selectedSegmentIds, segments, siteRole]);

  const isActuarialModelValid = useMemo(() => {
    if (totalSelected === 1 && selectedSegmentIds.length === 1) {
      const [{ segmentId }] = selectedSegmentIds;
      const { tag } = segments.find(({ id }) => id === segmentId);
      return tag === CONSTANTS.SEGMENT.TAG.ACTUARIAL;
    }
    return false;
  }, [totalSelected, selectedSegmentIds, segments]);

  const meetsValidationMatchedThreshold = useMemo(() => {
    if (isActuarialModelValid) {
      const [{ segmentId }] = selectedSegmentIds;
      const { clusters } = segments.find(({ id }) => id === segmentId);
      const meetsMatchedThreshold =
        clusters.find((c) => c.tag === 'ACTVAL')?.matched >= CONSTANTS.SEGMENT.MIN_VALIDATION_MATCHES;
      return meetsMatchedThreshold;
    }
    return false;
  }, [isActuarialModelValid, segments, selectedSegmentIds]);

  const validationMatchedCount = useMemo(() => {
    if (isActuarialModelValid) {
      return data.find((segment) => segment.id === selectedSegmentIds[0]?.segmentId)?.matched;
    }
    return 0;
  }, [data, isActuarialModelValid, selectedSegmentIds]);

  const modelSegments = useMemo(() => {
    const nextModelSegments = {};
    if (isClassifierModelValid || isActuarialModelValid) {
      selectedSegmentIds.forEach(({ segmentId }) => {
        const { tag } = segments.find(({ id }) => id === segmentId);
        switch (tag) {
          case CONSTANTS.SEGMENT.TAG.HIT: {
            nextModelSegments[CONSTANTS.TAL_MODEL.POSITIVE_SEGMENT_ID] = segmentId;
            break;
          }
          case CONSTANTS.SEGMENT.TAG.MISS: {
            nextModelSegments[CONSTANTS.TAL_MODEL.NEGATIVE_SEGMENT_ID] = segmentId;
            break;
          }
          case CONSTANTS.SEGMENT.TAG.VALIDATION: {
            nextModelSegments[CONSTANTS.TAL_MODEL.VALIDATION_SEGMENT_ID] = segmentId;
            break;
          }
          case CONSTANTS.SEGMENT.TAG.ACTUARIAL: {
            nextModelSegments[CONSTANTS.TAL_MODEL.ACTUARIAL_SEGMENT_ID] = segmentId;
            break;
          }
          default:
            break;
        }
      });
    }
    return nextModelSegments;
  }, [isClassifierModelValid, isActuarialModelValid, selectedSegmentIds, segments]);

  const checkEntityDeletable = useCallback(
    (entity) => {
      if (get(entity, 'type') === 'segments') checkDeleteSegment(entity.id);
    },
    [checkDeleteSegment]
  );

  // Columns
  const columns = useMemo(
    () => [
      ExpanderColumn,
      {
        Header: <Text textSize="small">Name</Text>,
        id: 'name',
        justify: 'left',
        accessor: (entity) => get(entity, 'dataset.name', get(entity, 'name')),
        isEditable: isAdmin,
        onEdit: (entity, name) => editEntityName(entity, name, editDataset, editSegment, editCluster),
        Cell: ({ value, row }) => CellName({ value, row, getDeprecatedTag: platformConfig.getDeprecatedTag }),
      },
      {
        Header: <Text textSize="small">Tag</Text>,
        id: 'tag',
        justify: 'left',
        accessor: (entity) => get(entity, 'tag', ''),
        Cell: CellTag,
        isEditable: isSuperAdmin,
        options: [
          CONSTANTS.SEGMENT.TAG.HIT,
          CONSTANTS.SEGMENT.TAG.MISS,
          CONSTANTS.SEGMENT.TAG.VALIDATION,
          CONSTANTS.SEGMENT.TAG.TARGET,
          CONSTANTS.SEGMENT.TAG.ACTUARIAL,
          CONSTANTS.SEGMENT.TAG.ACTVAL,
          CONSTANTS.SEGMENT.TAG['HI FREQ'],
          CONSTANTS.SEGMENT.TAG['LO FREQ'],
          CONSTANTS.SEGMENT.TAG.CLUSTER,
        ],
        onEdit: (entity, tag) => editEntityTag(entity, tag, editSegment, editCluster),
      },
      {
        Header: <Text textSize="small">Onboarded Records</Text>,
        id: 'records',
        justify: 'left',
        accessor: (entity) => {
          const filter = get(entity, 'filter', '');
          const segmentId = get(entity, 'segmentId');
          const users = Number(get(entity, 'users'));
          if (any([filter.includes('FREQ'), filter.startsWith('CLUSTERING'), Number.isNaN(users)])) return null;
          if (segmentId) {
            return get(data, `[${get(entity, 'parentIdx')}].dataset.payload.onb_validation_estimate`);
          }
          return users;
        },
        Cell: CellNumber,
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'matched',
        justify: 'left',
        sortType: numberSort,
        accessor: (entity) => {
          const matched = Number(get(entity, 'matched'));
          if (any([get(entity, 'filter', '').includes('FREQ'), Number.isNaN(matched)])) return null;
          return matched;
        },
        Cell: CellNumber,
      },
      {
        Header: <Text textSize="small">Match %</Text>,
        id: 'matchPercentage',
        justify: 'left',
        sortType: percentSort,
        accessor: (entity) => {
          const filter = get(entity, 'filter', '');
          const segmentId = get(entity, 'segmentId');
          const matched = Number(get(entity, 'matched'));
          const users = Number(get(entity, 'users'));
          if (
            any([filter.includes('FREQ'), filter.startsWith('CLUSTERING'), Number.isNaN(matched), Number.isNaN(users)])
          )
            return null;
          if (segmentId) {
            const validationRows = get(data, `[${get(entity, 'parentIdx')}].dataset.payload.onb_validation_estimate`);
            return validationRows ? `${((matched * 100) / validationRows).toFixed(1)} %` : '-';
          }
          return users !== 0 ? `${((matched * 100) / users).toFixed(1)} %` : '-';
        },
        Cell: CellText,
      },
      {
        Header: (
          <Text textSize="small" data-test="actions-header">
            Actions
          </Text>
        ),
        id: 'actions',
        disableSortBy: true,
        justify: 'left',
        accessor: (entity) =>
          CellActions({
            ...entity,
            client,
            isSandboxViewer,
            onOpenDownloadModal: () => setDownloadEntity(entity),
            onOpenDeleteModal: () => {
              setDeleteEntity(entity);
              checkEntityDeletable(entity);
            },
          }),
      },
      {
        Header: <Text textSize="small">Onboarded</Text>,
        id: 'onboarded',
        justify: 'center',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
    ],
    [
      isAdmin,
      isSuperAdmin,
      numberSort,
      percentSort,
      editDataset,
      editSegment,
      editCluster,
      platformConfig.getDeprecatedTag,
      data,
      client,
      isSandboxViewer,
      checkEntityDeletable,
    ]
  );

  const vizButtonTip = useMemo(() => {
    if (!totalSelected) return 'Select dataset(s)';
    if (isUpgradedPlatform) return 'Visualize functionality for non-legacy datasets is coming soon';
    return "Visualize dataset's explainable traits";
  }, [isUpgradedPlatform, totalSelected]);

  const hasData = !isLoading && data?.length;

  const visibilityDownloadEntry = useMemo(
    () => downloadEntity?.downloads.find((dl) => dl.id === downloadVisibilityId),
    [downloadEntity?.downloads, downloadVisibilityId]
  );
  const deleteDownloadEntry = useMemo(
    () => downloadEntity?.downloads.find((dl) => dl.id === downloadDeleteId),
    [downloadEntity?.downloads, downloadDeleteId]
  );

  useEffect(() => {
    history.replace({
      pathname: '/datasets',
      search: `?filter=${filterTag}`,
      state: {},
    });
  }, [filterTag, history]);

  useEffect(() => {
    if (
      editSegmentStatus === CONSTANTS.API.FAILED ||
      editDatasetStatus === CONSTANTS.API.FAILED ||
      editClusterStatus === CONSTANTS.API.FAILED
    ) {
      enqueueNotification('Editing the name failed. Please try again');
    }
  }, [editSegmentStatus, editDatasetStatus, enqueueNotification, editClusterStatus]);

  useEffect(() => {
    if (deleteSegmentStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Deleting the segment failed. Please try again');
    }
  }, [deleteSegmentStatus, enqueueNotification]);

  useEffect(() => {
    if (createModelStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Creating the model failed. Please try again');
    }
    if (createModelStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Model successfully created', { autoClose: true });
      setShowCreateClassifierModal(false);
      // setShowCreateActuarialModal(false);
      deselectAllSegmentIds();
    }
  }, [createModelStatus, deselectAllSegmentIds, enqueueNotification]);

  useEffect(() => {
    if (addModelTargetStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('We cannot predict the scores for the dataset now. Please try again.');
    }
    if (addModelTargetStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Batch predictions initiated', {
        autoClose: true,
        timeout: 4000,
      });
      setShowPredictModal(false);
      deselectAllSegmentIds();
      // allows backend events to finish before loading
      setTimeout(() => getData(), 5000);
    }
  }, [addModelTargetStatus, enqueueNotification, deselectAllSegmentIds, getData]);

  useEffect(() => {
    if (fetchDownloadUrlStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Download failed. Please try again later');
    }
    if (fetchDownloadUrlStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Download successfully completed', {
        autoClose: true,
      });
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl, fetchDownloadUrlStatus, enqueueNotification]);

  useEffect(() => {
    if (deleteDownloadStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Failed to delete download.');
    }
    if (deleteDownloadStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Deletion successfully completed', {
        autoClose: true,
      });
    }
  }, [deleteDownloadStatus, enqueueNotification]);

  /*
   * Fetch data whenever the tenant or params change
   */
  useEffect(() => {
    if (tenant && ![null, undefined].includes(filterTag)) {
      deselectAllSegmentIds();
      getData();
      fetchClientByTenant(tenant);
    }
  }, [tenant, filterTag, getData, fetchClientByTenant, deselectAllSegmentIds]);

  useEffect(() => {
    setIsAdmin(role === CONSTANTS.ROLE.ADMIN || siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
    setIsSuperAdmin(siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
    setIsSandboxViewer(CONSTANTS.SANDBOX_TENANT === originalTenant && role === CONSTANTS.ROLE.VIEWER);
  }, [originalTenant, role, siteRole]);

  useEffect(() => {
    if (has(downloadEntity, 'downloads')) {
      setShowEntityDownloadsModal(true);
    }
  }, [downloadEntity]);

  useEffect(() => {
    if (deleteEntity) {
      setShowDeleteEntityModal(true);
    }
  }, [deleteEntity]);

  function handleCloseEntityDownloads() {
    setShowEntityDownloadsModal(false);
    setDownloadEntity(null);
  }

  return (
    <Div flexDir="column" d="flex" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }}>
        <Col justify="left" align="center" gutterWidth="0" d="flex" size="6">
          {totalSelected < 2 ? (
            <Div data-test="visualize-btn-div">
              <Button
                buttonStyle="primary"
                data-tip={vizButtonTip}
                disabled={totalSelected === 0 || isUpgradedPlatform}
                onClick={() => {
                  if (selectedSegmentIds.length > 0) {
                    history.push(
                      ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(
                        ':entityId',
                        selectedSegmentIds[0].segmentId
                      )
                    );
                  }
                  if (selectedClusterIds.length > 0) {
                    history.push(
                      ROUTES.VIEW_SEGMENTS.replace(':entity', 'cluster').replace(
                        ':entityId',
                        selectedClusterIds[0].clusterId
                      )
                    );
                  }
                }}
              >
                <Icon name="Visualize" color="white" m={{ r: '0.5rem' }} size="1rem" />
                VISUALIZE
              </Button>
            </Div>
          ) : (
            <Button
              buttonStyle="primary"
              data-tip={
                isUpgradedPlatform
                  ? 'Visualize functionality for non-legacy datasets is coming soon'
                  : 'Compare explainable traits between datasets'
              }
              disabled={isUpgradedPlatform}
              onClick={() => {
                if (totalSelected > 1) {
                  const segmentsQs = selectedSegmentIds.map(({ segmentId }) => `segmentId=${segmentId}`);
                  const clustersQs = selectedClusterIds.map(({ clusterId }) => `clusterId=${clusterId}`);
                  history.push(`${ROUTES.COMPARE_SEGMENTS}?${[...segmentsQs, ...clustersQs].join('&')}`);
                }
              }}
            >
              <Icon name="Compare" color="white" m={{ r: '0.5rem' }} />
              COMPARE
            </Button>
          )}
          {client?.canPersonalize && (
            <Button
              buttonStyle="primary"
              disabled={!hasClusters}
              onClick={() => {
                history.push(ROUTES.CLUSTERS.replace(':segmentId', selectedSegmentIds[0].segmentId));
              }}
            >
              <Icon name="Clusters" color="white" m={{ r: '0.5rem' }} size="1rem" />
              CLUSTERS
            </Button>
          )}
          <CreateModelFeatureToggle
            client={client}
            isActuarialModelValid={isActuarialModelValid}
            isClassifierModelValid={isClassifierModelValid}
            isDeprecatedPlatform={isDeprecatedPlatform}
            meetsValidationMatchedThreshold={meetsValidationMatchedThreshold}
            role={role}
            showCreateActuarialModal={setShowCreateActuarialModal}
            showCreateClassifierModal={setShowCreateClassifierModal}
            siteRole={siteRole}
            tenant={tenant}
          />
          <PredictModelFeatureToggle
            tenant={tenant}
            isPredictModelValid={isPredictModelValid}
            showPredictModel={setShowPredictModal}
            client={client}
            isAdmin={isAdmin}
            isDeprecatedPlatform={isDeprecatedPlatform}
          />
        </Col>
        <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0">
          <Icon name="Filter" viewBox="0 0 17 16" size="1rem" m={{ x: '.625rem' }} data-tip="View filter" />
          <Button
            buttonStyle="tab"
            selected={filterTag === CONSTANTS.SEGMENT.FILTERS.ACTUARIAL}
            data-tip="View datasets for building Actuarial models"
            onClick={() => {
              deselectAllSegmentIds();
              setFilterTag(CONSTANTS.SEGMENT.FILTERS.ACTUARIAL);
            }}
          >
            <Icon name="ActuarialModel" size="1rem" viewBox="0 0 12 12" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">ACTUARIAL</Text>
          </Button>
          <Button
            buttonStyle="tab"
            selected={filterTag === CONSTANTS.SEGMENT.FILTERS.CLASSIFIER}
            data-tip="View datasets for building Classifier models"
            onClick={() => {
              deselectAllSegmentIds();
              setFilterTag(CONSTANTS.SEGMENT.FILTERS.CLASSIFIER);
            }}
          >
            <Icon name="ClassifierModel" size="1rem" viewBox="0 0 10.46 16" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">CLASSIFIER</Text>
          </Button>
          <Button
            buttonStyle="tab"
            selected={filterTag === CONSTANTS.SEGMENT.FILTERS.TARGET}
            data-tip="View datasets for writing out predictions from existing models"
            onClick={() => {
              deselectAllSegmentIds();
              setFilterTag(CONSTANTS.SEGMENT.FILTERS.TARGET);
            }}
          >
            <Icon name="TargetNew" size="1rem" viewBox="0 0 50 50" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">TARGET</Text>
          </Button>
          <Button
            buttonStyle="tab"
            m={{ r: '0rem' }}
            selected={['', CONSTANTS.SEGMENT.FILTERS.ALL].includes(filterTag)}
            data-tip="View all dataset types"
            onClick={() => {
              deselectAllSegmentIds();
              setFilterTag(CONSTANTS.SEGMENT.FILTERS.ALL);
            }}
          >
            <Text textSize="body">ALL</Text>
          </Button>

          <Div
            minH="1.5rem"
            minW="1px"
            m={{ x: '1rem' }}
            border={{ r: '1px solid' }}
            borderColor="shade66"
            key="filter-spacer"
          />

          <Button
            buttonStyle="tab"
            selected={view === 'LIST_VIEW'}
            w="1.875rem"
            onClick={() => {
              setView('LIST_VIEW');
            }}
            data-tip="View datasets as line items"
          >
            <Icon color="white" name="ListView" viewBox="0 0 40 40" size="1.15rem" />
          </Button>
          <Button
            buttonStyle="tab"
            selected={view === 'CARD_VIEW'}
            m={{ r: '0' }}
            w="1.875rem"
            onClick={() => {
              setView('CARD_VIEW');
            }}
            data-tip="View datasets as cards"
          >
            <Icon color="white" name="CardView" viewBox="0 0 40 40" size="1.15rem" />
          </Button>
        </Col>
      </Row>

      <Row h="100%" data-debug="datasets-row" m={{ x: '1.5rem', b: '.5rem' }}>
        <Col size="12" rounded="md" bg={hasData ? 'white' : ''} gutterWidth="0">
          <Row
            data-debug="datasets-table-row"
            m={{ t: '1rem', b: '.75rem' }}
            bg="white"
            rounded={{ bl: 'md', br: 'md' }}
            flexDir="row"
            align="center"
          >
            <Col size="12">
              {view === 'LIST_VIEW' ? (
                // @ts-ignore
                <GridWrapper
                  hasData={hasData}
                  getData={getData}
                  columns={columns}
                  data={data}
                  gridState={gridState}
                  controlledGridState={controlledGridState}
                  rawData={segments}
                  onRowClick={(row) => {
                    setSegmentId(row.id);
                    reset();
                    history.push(`/segments/${row.id}`);
                  }}
                  enableSelection={({ original }) => original.matched > 0}
                  selectionTooltip={({ original }) => {
                    if (original.matched === 0) return 'Zero matches; functions unavailable.';
                    if (original.matched < CONSTANTS.MIN_SEGMENT_MATCHES)
                      `Under ${CONSTANTS.MIN_SEGMENT_MATCHES} matches; analytics unavailable.`;
                    return 'Select';
                  }}
                  onRowSelect={(row) => {
                    selectGridRow(row);
                  }}
                  onRowDeselect={(row) => {
                    deselectGridRow(row);
                  }}
                  hasSelection
                  hasExpansion
                  maxSelection={maxGridRowSelection}
                  apiStatus={checkApiStatus(error, isLoading)}
                  setGridState={(state) => {
                    setGridState(state);
                  }}
                  subRowsColsIndentIndexes={[1, 2, 3]}
                  DivProps={{ m: '0rem', p: '0rem', 'data-test': 'segments-grid-wrapper' }}
                  TableProps={{ p: { y: '0rem' }, overflow: 'hidden', 'data-test': 'segments-table' }}
                  widths="0.4fr 0.4fr 3fr 2fr 1fr 1fr 1fr 1fr 1fr"
                />
              ) : (
                <ApiWrapper hasData={hasData} getData={getData} apiStatus={checkApiStatus(error, isLoading)}>
                  <Row m={{ t: '1rem', x: '-.5rem' }}>
                    {data.map((datum, index) => (
                      <>
                        <Col
                          size={{
                            xs: '12',
                            md: '6',
                            lg: '6',
                            xl: '4',
                          }}
                          gutterWidth="1.25em"
                        >
                          <SegmentCard
                            datum={datum}
                            index={index}
                            selectedSegmentIds={selectedSegmentIds}
                            selectedClusterIds={selectedClusterIds}
                            selectGridRow={selectGridRow}
                            deselectGridRow={deselectGridRow}
                            onThemeChange={(theme) => {
                              editSegment(datum.id, { theme });
                            }}
                          />
                        </Col>
                        {datum.subRows.map((subRowDatum, subRowIndex) => (
                          <Col
                            key={`${subRowDatum.segmentId}-${subRowDatum.id}`}
                            size={{
                              xs: '12',
                              md: '6',
                              lg: '6',
                              xl: '4',
                            }}
                            gutterWidth="1.25em"
                          >
                            <SegmentCard
                              datum={subRowDatum}
                              index={`${index}-${subRowIndex}`}
                              selectedSegmentIds={selectedSegmentIds}
                              selectedClusterIds={selectedClusterIds}
                              selectGridRow={selectGridRow}
                              deselectGridRow={deselectGridRow}
                              onThemeChange={(theme) => {
                                editCluster(subRowDatum.segmentId, subRowDatum.id, { theme });
                              }}
                            />
                          </Col>
                        ))}
                      </>
                    ))}
                  </Row>
                </ApiWrapper>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <CreateClassifierModelDialog
        isOpen={showCreateClassifierModal}
        actions={{
          close: {
            on: () => setShowCreateClassifierModal(false),
          },
          cancel: {},
          handlers: [
            {
              on: (thinkalikeModelData) => {
                createModel({
                  ...thinkalikeModelData,
                  positive_segment_id: modelSegments[CONSTANTS.TAL_MODEL.POSITIVE_SEGMENT_ID],
                  negative_segment_id: modelSegments[CONSTANTS.TAL_MODEL.NEGATIVE_SEGMENT_ID],
                  validation_segment_id: modelSegments[CONSTANTS.TAL_MODEL.VALIDATION_SEGMENT_ID],
                  dataModelType: 'CLASSIFIER',
                  dataModelSubType: '1_fold',
                  all_lift_info: {},
                  lift_scores: {},
                  payload: {
                    profile,
                  },
                });
              },
            },
          ],
        }}
      />
      <CreateActuarialModelDialog
        isOpen={showCreateActuarialModal}
        tenant={tenant}
        actions={{
          close: {
            on: () => {
              setShowCreateActuarialModal(false);
            },
          },
          cancel: {},
          handlers: [
            {
              before: getFileUploadUrl,
              on: uploadFileLowSec,
              after: async (validationInfo) => {
                if (!validationInfo) return false;
                const query = {
                  filters: [
                    {
                      name: 'dataset_id',
                      op: 'eq',
                      val: selectedSegmentIds[0].datasetId,
                    },
                  ],
                };
                const onboardings = await fetchOnboardings(query, {
                  headers: {
                    'X-Fields':
                      'items{id,files{states{id,state,payload{eng_px_profile_plus_path,eng_user_word_path}}}},*',
                  },
                });
                let ssl = onboardings?.items?.[0]?.files?.[0]?.states
                  ?.toSorted?.((a, b) => b.id - a.id)
                  ?.find?.((st) => st.state === 'PROFILE_MATCHED')?.payload?.eng_px_profile_plus_path;
                if (!ssl) {
                  ssl = onboardings?.items?.[0]?.files?.[0]?.states
                    ?.toSorted?.((a, b) => b.id - a.id)
                    ?.find?.((st) => st.state === 'PROFILE_MATCHED')?.payload?.eng_user_word_path;
                }
                let ss = null;
                if (ssl) {
                  const parts = ssl.split('/');
                  const ssBucket = parts[1];
                  const ssKey = `${parts
                    .slice(2)
                    .join('/')
                    .replace('/coalesce', '')
                    .split('/')
                    .slice(0, -1)
                    .join('/')}`;
                  ss = {
                    bucket: ssBucket,
                    key: ssKey,
                  };
                }

                const pyld = {
                  ...validationInfo,
                  ss,
                };
                return validateReplacedOutcomes(pyld);
              },
            },
            {
              on: (thinkalikeModelData) => {
                createModel({
                  ...thinkalikeModelData,
                  actuarialSegmentId: modelSegments[CONSTANTS.TAL_MODEL.ACTUARIAL_SEGMENT_ID],
                  dataModelType: 'ACTUARIAL',
                  dataModelSubType: '10_fold',
                  allLiftInfo: {},
                  liftScores: {},
                  payload: {
                    profile,
                  },
                });
              },
            },
          ],
        }}
        meetsValidationMatchedThreshold={meetsValidationMatchedThreshold}
        validationMatchedCount={validationMatchedCount}
        refresh={segments.find(({ id }) => id === selectedSegmentIds[0]?.segmentId)?.refresh}
      />
      <PredictModelDialog
        isOpen={showPredictModal}
        actions={{
          close: {
            on: () => setShowPredictModal(false),
          },
          cancel: {
            on: () => deselectAllSegmentIds(),
          },
          handlers: [
            {
              on: (modelData) => {
                const { id } = modelData?.model ?? {};
                const [{ segmentId }] = selectedSegmentIds;
                const payload = {
                  segmentId,
                  config_metadata: {
                    profile,
                  },
                };
                addModelTarget(id, payload);
              },
            },
          ],
        }}
        selectedSegment={segments.find(({ id }) => id === selectedSegmentIds[0]?.segmentId)}
      />
      <DeleteSegmentDialog
        isOpen={showDeleteEntityModal}
        dependentResources={checkDeleteSegmentDependents}
        type={get(deleteEntity, 'type', '').slice(0, -1)}
        actions={{
          close: {
            on: () => {
              setShowDeleteEntityModal(false);
              window.setTimeout(() => unset('checkDeleteSegmentDependents'), 500);
            },
          },
          cancel: {
            on: () => {},
          },
          handlers: [
            {
              on: () => {
                const id = get(deleteEntity, 'id');
                if (get(deleteEntity, 'type') === 'segments') deleteSegment(id);
                if (get(deleteEntity, 'type') === 'clusters') deleteCluster(get(deleteEntity, 'segmentId'), id);
              },
            },
          ],
        }}
      />
      <DownloadSegmentDialog
        isOpen={showEntityDownloadsModal}
        id={get(downloadEntity, 'id')}
        downloads={get(downloadEntity, 'downloads', [])}
        statuses={get(downloadEntity, 'statuses', [])}
        tag={get(downloadEntity, 'tag')}
        role={role}
        siteRole={siteRole}
        actions={{
          close: {
            on: handleCloseEntityDownloads,
          },
          cancel: {
            on: () => {},
          },
          handlers: [
            {
              // Download button
              on: (downloadId) => {
                const { entityId, type } = downloadEntity.downloads.find((dl) => dl.id === downloadId);
                fetchDownloadUrl(entityId, type, downloadId);
              },
            },
            {
              // Delete button
              on: (deleteId, name) => {
                setShowDeleteDownloadModal(true);
                setDownloadName(name);
                setDownloadDeleteId(deleteId);
              },
            },
            {
              // Visibility button
              on: (visibilityId, name) => {
                setShowVisibilityDownloadModal(true);
                setDownloadName(name);
                setDownloadVisibilityId(visibilityId);
              },
            },
          ],
        }}
      />
      <DeleteSegmentDownloadDialog
        isOpen={showDeleteDownloadModal}
        name={downloadName}
        actions={{
          close: {
            on: () => {
              setShowDeleteDownloadModal(false);
              setDownloadName(null);
              setDownloadDeleteId(null);
              setShowDeleteDownloadModal(false);
            },
          },
          cancel: {
            on: () => {},
          },
          handlers: [
            {
              on: () => {
                deleteDownload(deleteDownloadEntry?.entityId, deleteDownloadEntry?.type, downloadDeleteId);
              },
              after: () => {
                handleCloseEntityDownloads();
                getData();
              },
            },
          ],
        }}
      />
      <VisibilitySegmentDownloadDialog
        isOpen={showVisibilityDownloadModal}
        download={downloadEntity?.downloads?.find((dl) => dl.id === downloadVisibilityId)}
        actions={{
          close: {
            on: () => {
              setShowVisibilityDownloadModal(false);
              setDownloadName(null);
              setDownloadVisibilityId(null);
              setShowVisibilityDownloadModal(false);
            },
          },
          cancel: {
            on: () => {},
          },
          handlers: [
            {
              on: () => {
                toggleVisibilityDownload(
                  visibilityDownloadEntry?.entityId,
                  visibilityDownloadEntry?.type,
                  downloadVisibilityId,
                  visibilityDownloadEntry?.hiddenByRole === 'ADMIN' ? null : 'ADMIN'
                );
              },
              after: () => {
                handleCloseEntityDownloads();
                getData();
              },
            },
          ],
        }}
      />
    </Div>
  );
}

function CellDate({ value }) {
  return (
    <Text
      data-tip={dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY')}
      m={{ r: '0.5rem' }}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {dayjs(value).format('MMM DD, YYYY')}
    </Text>
  );
}

CellDate.propTypes = {
  value: PropTypes.string.isRequired,
};

function CellName({ value, row, getDeprecatedTag }) {
  const { platform } = row?.original?.refresh ?? {};
  return (
    <>
      <DeprecatedTag tag={getDeprecatedTag(platform)} m={{ r: '0.5rem' }} />
      <Text
        data-tip={value}
        m={{ r: '0.5rem' }}
        textSize="small"
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Text>
    </>
  );
}

CellName.defaultProps = {
  getDeprecatedTag: () => null,
};

CellName.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      refresh: PropTypes.shape({
        platform: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  getDeprecatedTag: PropTypes.func,
};

function CellText({ value }) {
  return (
    <Text
      data-tip={value}
      m={{ r: '0.5rem' }}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Text>
  );
}

CellText.propTypes = {
  value: PropTypes.string.isRequired,
};

function CellNumber({ value }) {
  const number = formatNumber(value);
  return (
    <Text
      data-tip={number}
      m={{ r: '0.5rem' }}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {number}
    </Text>
  );
}

CellNumber.propTypes = {
  value: PropTypes.string.isRequired,
};

function getBoolLabel(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '-';
}

function CellBool({ value }) {
  return (
    <Text
      data-tip={getBoolLabel(value)}
      m={{ r: '0.5rem' }}
      textSize="small"
      style={{
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {getBoolLabel(value)}
    </Text>
  );
}

CellBool.propTypes = {
  value: PropTypes.bool.isRequired,
};

function CellTag({ value }) {
  if (!CONSTANTS.SEGMENT.TAGS.includes(value)) return <Tag bg="transparent">&ndash; &ndash; &ndash;</Tag>;
  const { color, name, viewBox, tip } = getSegmentTagStyles(value);
  return (
    <Tag
      p={{ x: '.375rem', y: '.188rem' }}
      // @ts-ignore
      h="1.125rem"
      textColor={color}
      borderColor={color}
      border="1px solid"
      data-tip={tip}
    >
      <Icon name={name} size=".75rem" viewBox={viewBox} color={color} m={{ r: '.25rem' }} />
      <Text textSize="small">{CONSTANTS.SEGMENT.TAG[value]}</Text>
    </Tag>
  );
}

CellTag.propTypes = {
  value: PropTypes.string.isRequired,
};

function CellActions({
  id,
  client,
  dataset,
  downloads,
  filter,
  type,
  isSandboxViewer,
  onOpenDeleteModal,
  onOpenDownloadModal,
  statuses,
  tag,
}) {
  return (
    <Div d="flex" align="center" justify="center" h="1.5rem" m={{ r: '0.5rem' }}>
      <Div data-test="del-button-div">
        <DeleteComponent type="dataset" onOpenModal={onOpenDeleteModal} iconSize=".75rem" />
      </Div>
      <DownloadComponent
        id={id}
        client={client}
        dataset={dataset}
        downloads={downloads}
        filter={filter}
        iconSize=".75rem"
        isSandboxViewer={isSandboxViewer}
        onOpenModal={onOpenDownloadModal}
        statuses={statuses}
        tag={tag}
      />
    </Div>
  );
}

CellActions.propTypes = {
  id: PropTypes.number.isRequired,
  client: PropTypes.shape({
    canPersonalize: PropTypes.bool,
  }).isRequired,
  dataset: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }).isRequired,
  downloads: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filter: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  isSandboxViewer: PropTypes.bool.isRequired,
  onOpenDeleteModal: PropTypes.func.isRequired,
  onOpenDownloadModal: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tag: PropTypes.string.isRequired,
};

const mapStateToProps = ({ app, bc, segmentsList }, ownProps) => ({
  app,
  bc,
  segmentsList,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  addModelTarget: (...params) => dispatch(segmentsListActions.addModelTarget(...params)),
  createModel: (...params) => dispatch(segmentsListActions.createModel(...params)),
  deleteCluster: (...params) => dispatch(segmentsListActions.deleteCluster(...params)),
  deleteDownload: (...params) => dispatch(segmentsListActions.deleteDownload(...params)),
  toggleVisibilityDownload: (...params) => dispatch(segmentsListActions.toggleVisibilityDownload(...params)),
  checkDeleteSegment: (...params) => dispatch(segmentsListActions.checkDeleteSegment(...params)),
  deleteSegment: (...params) => dispatch(segmentsListActions.deleteSegment(...params)),
  deselectAllSegmentIds: () => dispatch(segmentsListActions.deselectAllSegmentIds()),
  deselectGridRow: (segmentId) => dispatch(segmentsListActions.deselectGridRow(segmentId)),
  editCluster: (...params) => dispatch(segmentsListActions.editCluster(...params)),
  editDataset: (...params) => dispatch(segmentsListActions.editDataset(...params)),
  editSegment: (...params) => dispatch(segmentsListActions.editSegment(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  fetchClientByTenant: (...params) => dispatch(segmentsListActions.fetchClientByTenant(...params)),
  fetchDatasets: (query) => dispatch(appActions.fetchDatasets(query)),
  fetchDownloadUrl: (...params) => dispatch(segmentsListActions.fetchDownloadUrl(...params)),
  reset: () => dispatch(bcActions.reset()),
  selectGridRow: (segmentId) => dispatch(segmentsListActions.selectGridRow(segmentId)),
  setDatasetId: (datasetId) => dispatch(bcActions.setDatasetId(datasetId)),
  setDatasetName: (name) => dispatch(bcActions.setDatasetName(name)),
  setGridState: (state) => dispatch(segmentsListActions.setGridState(state)),
  setSegmentId: (segmentId) => dispatch(appActions.setSegmentId(segmentId)),
  unset: (key) => dispatch(segmentsListActions.unset(key)),
});

Segments.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired, replace: PropTypes.func.isRequired }).isRequired,
  app: PropTypes.shape({
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ).isRequired,
    fetchDatasetsStatus: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      siteRole: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
    }),
  }).isRequired,
  segmentsList: PropTypes.shape({
    checkDeleteSegmentDependents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    addModelTargetStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    client: PropTypes.shape({
      canPersonalize: PropTypes.bool,
    }).isRequired,
    controlledGridState: PropTypes.shape({}).isRequired,
    createModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    deleteClusterStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    deleteDownloadStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    deleteSegmentStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    downloadUrl: PropTypes.string.isRequired,
    editClusterStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    editDatasetStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    editSegmentStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchDownloadUrlStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    gridState: PropTypes.shape({}).isRequired,
    maxGridRowSelection: PropTypes.number.isRequired,
    selectedClusterIds: PropTypes.arrayOf(
      PropTypes.shape({
        clusterId: PropTypes.number,
      })
    ).isRequired,
    selectedSegmentIds: PropTypes.arrayOf(
      PropTypes.shape({
        segmentId: PropTypes.number,
        datasetId: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,
  addModelTarget: PropTypes.func.isRequired,
  createModel: PropTypes.func.isRequired,
  deleteCluster: PropTypes.func.isRequired,
  deleteDownload: PropTypes.func.isRequired,
  toggleVisibilityDownload: PropTypes.func.isRequired,
  deleteSegment: PropTypes.func.isRequired,
  checkDeleteSegment: PropTypes.func.isRequired,
  deselectAllSegmentIds: PropTypes.func.isRequired,
  deselectGridRow: PropTypes.func.isRequired,
  editCluster: PropTypes.func.isRequired,
  editDataset: PropTypes.func.isRequired,
  editSegment: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  fetchClientByTenant: PropTypes.func.isRequired,
  fetchDownloadUrl: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  selectGridRow: PropTypes.func.isRequired,
  setGridState: PropTypes.func.isRequired,
  setSegmentId: PropTypes.func.isRequired,
  unset: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Segments));
